/**
 * Course "Meetings" is one logical section split across two named routes
 * (the Upcoming / Previous subviews). 
 * @type {readonly string[]}
 */
export const COURSE_MEETINGS_ROUTE_NAMES = Object.freeze([
    'course-meetings-view-upcoming',
    'course-meetings-view-previous',
]);

/**
 * @param {string|null|undefined} routeName
 * @returns {boolean}
 */
export function isCourseMeetingsRouteName(routeName) {
    return COURSE_MEETINGS_ROUTE_NAMES.includes(routeName);
}

/**
 * Determine whether a menu route matches the current route exactly.
 *
 * @param {object|null|undefined} targetRoute
 * @param {object} currentRoute
 * @param {{ resolve: (route: object) => { name?: string, params?: object } }} router
 * @returns {boolean}
 */
export function isRouteExactlyActive(targetRoute, currentRoute, router) {
    if (!targetRoute || !currentRoute?.name) {
        return false;
    }

    const resolved = router.resolve(targetRoute);

    if (resolved.name !== currentRoute.name) {
        return false;
    }

    const paramKeys = Object.keys(resolved.params || {});

    if (paramKeys.length === 0) {
        return true;
    }

    return paramKeys.every(key => String(currentRoute.params[key]) === String(resolved.params[key]));
}

/**
 * @param {Array} nodes
 * @param {string} targetKey
 * @returns {boolean}
 */
function nodeTreeContainsKey(nodes, targetKey) {
    return nodes.some(node => {
        if (node.key === targetKey) {
            return true;
        }

        if (node.children?.length) {
            return nodeTreeContainsKey(node.children, targetKey);
        }

        return false;
    });
}

/**
 * @param {Array} topLevelNodes
 * @param {string} activeKey
 * @returns {string|null}
 */
export function findTopLevelSummaryKey(topLevelNodes, activeKey) {
    if (!activeKey) {
        return null;
    }

    const topLevelMatch = topLevelNodes.find(node => node.key === activeKey);

    if (topLevelMatch) {
        return topLevelMatch.key;
    }

    const parentMatch = topLevelNodes.find(
        node => node.children?.length && nodeTreeContainsKey(node.children, activeKey)
    );

    return parentMatch?.key ?? null;
}

/**
 * Find the tree node and ancestor expansion keys that match the current route.
 *
 * @param {Array} nodes
 * @param {object} currentRoute
 * @param {{ resolve: (route: object) => { name?: string, params?: object } }} router
 * @param {string[]} parentKeys
 * @returns {{ selectionKeys: object, expandedKeys: object, activeKey: string|null }|null}
 */
export function findActiveMenuNodeState(nodes, currentRoute, router, parentKeys = []) {
    const activeNode = nodes.find(
        node => node.data?.route && isRouteExactlyActive(node.data.route, currentRoute, router)
    );

    if (activeNode) {
        const expandedKeys = parentKeys.reduce((accumulator, key) => {
            accumulator[key] = true;
            return accumulator;
        }, {});

        return {
            selectionKeys: { [activeNode.key]: true },
            expandedKeys,
            activeKey: activeNode.key,
        };
    }

    return nodes.reduce((matchedState, node) => {
        if (matchedState || !node.children?.length) {
            return matchedState;
        }

        return findActiveMenuNodeState(node.children, currentRoute, router, [...parentKeys, node.key]);
    }, null);
}

/**
 * Resolve sidebar highlight state for the current route.
 *
 * @param {Array} topLevelNodes
 * @param {object} currentRoute
 * @param {{ resolve: (route: object) => { name?: string, params?: object } }} router
 * @returns {{ selectionKeys: object, expandedKeys: object, activeSummaryKey: string|null }}
 */
export function resolveSidebarMenuActiveState(topLevelNodes, currentRoute, router) {
    const matchedState = findActiveMenuNodeState(topLevelNodes, currentRoute, router);

    if (matchedState) {
        return {
            selectionKeys: matchedState.selectionKeys,
            expandedKeys: matchedState.expandedKeys,
            activeSummaryKey: findTopLevelSummaryKey(topLevelNodes, matchedState.activeKey),
        };
    }

    if (currentRoute.name === 'home') {
        return {
            selectionKeys: { home: true },
            expandedKeys: {},
            activeSummaryKey: 'home',
        };
    }

    return {
        selectionKeys: {},
        expandedKeys: {},
        activeSummaryKey: null,
    };
}
